/**
 * App Storage class
 *
 * @description Just a wrapper over LocalStorage and SessionStorage.
 * This is to restrict the usage of Global window storage throughtout the application
 *
 * IMPORTANT NOTE!!!
 * Don’t store sensitive information in the web storage.
 */
import RandomUtils from "@/utils/random.utils";

class AppStorage {
  constructor(storage) {
    this.storage = storage;

    if (!this.isSupported()) {
      throw new Error("Storage is not supported by browser!");
    }
  }

  setItem(key, value) {
    try {
      // try-catch here as setItem will throw exception if storage is full, or if using private mode in safari
      value = JSON.stringify(value);
      this.storage.setItem(key, value);
    } catch {
      // Shouldn't happen
    }
  }

  getItem(key) {
    return this.storage.getItem(key);
  }

  removeItem(key) {
    this.storage.removeItem(key);
  }

  clear() {
    this.storage.clear();
  }

  getAndRemoveItem(key) {
    let item = sessionStorage.getItem(key);
    if (item) {
      item = JSON.parse(item);
    }
    sessionStorage.removeItem(key);
    return item;
  }

  isSupported() {
    let supported = true;

    if (!this.storage) {
      supported = false;
    }

    return supported;
  }
}

const BROWSER_TAB_UUID_KEY = "browserTabUuid";
let browserTabUuid: string | null = null;
const appLocalStorage = new AppStorage(window.localStorage);
const appSessionStorage = new AppStorage(window.sessionStorage);

const isPageReload = (): boolean => {
  const [navEntry] = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
  return navEntry?.type === "reload";
};

const getBrowserTabUuid = (): string => {
  if (!browserTabUuid) {
    // Reuse the persisted UUID ONLY on a genuine same-tab refresh. sessionStorage survives a
    // reload, so this keeps the tab UUID stable and avoids a false "locked by same user in
    // another tab" result after refreshing while editing.
    //
    // On any other load (fresh navigation, or a child/duplicated tab that inherited a copy of
    // the opener's sessionStorage in Chromium), mint a new UUID and overwrite the stored value
    // so a genuinely different tab is still treated as a different tab by the lock backend.
    const stored = appSessionStorage.getItem(BROWSER_TAB_UUID_KEY);
    if (stored && isPageReload()) {
      browserTabUuid = JSON.parse(stored);
    } else {
      browserTabUuid = RandomUtils.randomUuid();
      appSessionStorage.setItem(BROWSER_TAB_UUID_KEY, browserTabUuid);
    }
  }

  return browserTabUuid;
};

export { appLocalStorage, appSessionStorage, getBrowserTabUuid };
